import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Play, Pause, X, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import { createBreathingAudio } from '../../utils/audioEngine';

const BreathingPlayer = ({ config, taskTitle, onComplete, onClose }) => {
  const { phases, rounds, color } = config;
  const cycleDuration = useMemo(() => phases.reduce((sum, p) => sum + p.duration, 0), [phases]);
  const totalDuration = cycleDuration * rounds;

  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [audioOn, setAudioOn] = useState(false);
  const intervalRef = useRef(null);
  const audioRef = useRef(null);
  const prevPhaseRef = useRef(-1);

  // Derive state from elapsed
  const currentCycleElapsed = elapsed % cycleDuration;
  let phaseIdx = 0;
  let timeInPhase = currentCycleElapsed;
  for (let i = 0; i < phases.length; i++) {
    if (timeInPhase < phases[i].duration) { phaseIdx = i; break; }
    timeInPhase -= phases[i].duration;
  }
  const currentPhase = phases[phaseIdx];
  const secondsLeft = Math.ceil(currentPhase.duration - timeInPhase);
  const currentRound = Math.min(Math.floor(elapsed / cycleDuration) + 1, rounds);
  const progress = Math.min(elapsed / totalDuration, 1);

  const isInhale = currentPhase.name === 'inhale';
  const isExhale = currentPhase.name === 'exhale';
  const circleScale = isExhale ? 0.55 : 1;
  const transitionDur = started ? currentPhase.duration : 0.3;
  const glowIntensity = isInhale ? 0.5 : isExhale ? 0.15 : 0.35;
  const ringSize = isInhale ? 80 : isExhale ? 30 : 60;

  // Audio: sync phase changes
  useEffect(() => {
    if (audioOn && audioRef.current && audioRef.current.isRunning() && prevPhaseRef.current !== phaseIdx) {
      audioRef.current.setPhase(currentPhase.name, currentPhase.duration);
    }
    prevPhaseRef.current = phaseIdx;
  }, [phaseIdx, audioOn, currentPhase]);

  // Timer
  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setElapsed(prev => {
        const next = prev + 1;
        if (next >= totalDuration) {
          clearInterval(intervalRef.current);
          setRunning(false);
          setFinished(true);
          return totalDuration;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running, totalDuration]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => { if (audioRef.current) audioRef.current.stop(); };
  }, []);

  const toggleAudio = () => {
    if (audioOn) {
      if (audioRef.current) audioRef.current.stop();
      audioRef.current = null;
      setAudioOn(false);
    } else {
      try {
        const engine = createBreathingAudio();
        engine.start();
        if (running) engine.setPhase(currentPhase.name, currentPhase.duration);
        audioRef.current = engine;
        setAudioOn(true);
      } catch {}
    }
  };

  const handleStart = () => {
    setStarted(true);
    setRunning(true);
    if (!audioRef.current) {
      try {
        const engine = createBreathingAudio();
        engine.start();
        audioRef.current = engine;
        setAudioOn(true);
      } catch {}
    }
  };
  const handlePause = () => setRunning(false);
  const handleResume = () => setRunning(true);

  const handleReset = () => {
    clearInterval(intervalRef.current);
    if (audioRef.current) { audioRef.current.stop(); audioRef.current = null; }
    setAudioOn(false);
    setElapsed(0); setRunning(false); setStarted(false); setFinished(false);
    prevPhaseRef.current = -1;
  };

  const handleFinish = useCallback(() => {
    if (audioRef.current) { audioRef.current.stop(); audioRef.current = null; }
    onComplete({
      started: true, duration_played: elapsed, total_duration: totalDuration,
      rounds_completed: Math.floor(elapsed / cycleDuration), task_type: 'interactive_breathing',
    });
  }, [elapsed, totalDuration, cycleDuration, onComplete]);

  return (
    <div className="flex flex-col items-center justify-center h-full w-full px-6 select-none" data-testid="breathing-player">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4 z-10">
        <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 transition" data-testid="breathing-close">
          <X className="w-5 h-5 text-white/40" />
        </button>
        <span className="text-white/30 text-xs font-medium tracking-wider uppercase">{taskTitle}</span>
        <div className="w-9" />
      </div>

      {!started && !finished ? (
        <div className="flex flex-col items-center gap-8 animate-fadeIn">
          <div className="w-32 h-32 rounded-full flex items-center justify-center" style={{
            background: `radial-gradient(circle, ${color}30, ${color}08)`,
            boxShadow: `0 0 60px ${color}20`,
          }}>
            <div className="w-20 h-20 rounded-full" style={{
              background: `radial-gradient(circle, ${color}50, ${color}15)`,
              boxShadow: `0 0 30px ${color}30`,
            }} />
          </div>
          <div className="text-center space-y-3">
            <h2 className="text-white text-2xl font-bold">{taskTitle}</h2>
            <p className="text-white/40 text-sm max-w-xs mx-auto">
              {phases.map(p => `${p.label} ${p.duration}s`).join(' · ')} — {rounds} tur
            </p>
          </div>
          <button onClick={handleStart}
            className="px-10 py-4 rounded-2xl text-base font-bold text-white transition-all hover:scale-105 active:scale-95"
            style={{ background: `linear-gradient(135deg, ${color}, ${color}BB)`, boxShadow: `0 12px 40px ${color}40` }}
            data-testid="breathing-start">
            Başla
          </button>
        </div>
      ) : finished ? (
        <div className="flex flex-col items-center gap-8 animate-fadeIn">
          <div className="w-28 h-28 rounded-full flex items-center justify-center" style={{ background: 'rgba(52,211,153,0.15)', boxShadow: '0 0 60px rgba(52,211,153,0.2)' }}>
            <svg className="w-14 h-14 text-emerald-400" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-white text-2xl font-bold">Tamamlandı!</h2>
            <p className="text-white/40 text-sm">{rounds} tur nefes egzersizi tamamlandı</p>
          </div>
          <div className="flex gap-3">
            <button onClick={handleReset} className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium text-white/50 hover:text-white/70 transition" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <RotateCcw className="w-4 h-4" /> Tekrarla
            </button>
            <button onClick={handleFinish} className="px-8 py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #34D399, #34D399BB)', boxShadow: '0 8px 30px rgba(52,211,153,0.3)' }} data-testid="breathing-finish">
              Devam Et
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-6 w-full max-w-sm">
          <div className="text-white/25 text-xs font-semibold tracking-widest uppercase">
            Tur {currentRound} / {rounds}
          </div>

          {/* Breathing circle */}
          <div className="relative flex items-center justify-center" style={{ width: 240, height: 240 }}>
            <div className="absolute inset-0 rounded-full transition-all" style={{
              background: `radial-gradient(circle, ${color}${Math.round(glowIntensity * 30).toString(16).padStart(2, '0')}, transparent 70%)`,
              transform: `scale(${circleScale * 1.3})`,
              transition: `transform ${transitionDur}s ease-in-out, background ${transitionDur}s ease-in-out`,
            }} />
            <div className="absolute rounded-full flex items-center justify-center flex-col" style={{
              width: 180, height: 180,
              transform: `scale(${circleScale})`,
              transition: `transform ${transitionDur}s ease-in-out, box-shadow ${transitionDur}s ease-in-out`,
              background: `radial-gradient(circle at 30% 30%, ${color}40, ${color}10)`,
              boxShadow: `0 0 ${ringSize}px ${color}${Math.round(glowIntensity * 255).toString(16).padStart(2, '0')}, inset 0 0 40px ${color}15`,
              border: `2px solid ${color}30`,
            }}>
              <span className="text-white font-bold text-xl tracking-wide" style={{ textShadow: `0 0 20px ${color}60` }}>
                {currentPhase.label}
              </span>
              <span className="text-white/60 text-4xl font-light mt-1" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {secondsLeft}
              </span>
            </div>
          </div>

          {/* Phase dots */}
          <div className="flex items-center gap-2">
            {phases.map((p, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full transition-all duration-500" style={{
                  background: i === phaseIdx ? color : 'rgba(255,255,255,0.1)',
                  boxShadow: i === phaseIdx ? `0 0 8px ${color}80` : 'none',
                }} />
                <span className="text-[10px] font-medium transition-colors duration-300" style={{
                  color: i === phaseIdx ? color : 'rgba(255,255,255,0.2)',
                }}>{p.label}</span>
              </div>
            ))}
          </div>

          {/* Progress bar */}
          <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div className="h-full rounded-full transition-all duration-1000 ease-linear" style={{
              width: `${progress * 100}%`,
              background: `linear-gradient(90deg, ${color}, ${color}CC)`,
              boxShadow: `0 0 10px ${color}60`,
            }} />
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4 mt-2">
            <button onClick={toggleAudio} className="w-10 h-10 rounded-full flex items-center justify-center transition-all" style={{
              background: audioOn ? `${color}20` : 'rgba(255,255,255,0.04)',
              border: `1px solid ${audioOn ? `${color}30` : 'rgba(255,255,255,0.06)'}`,
            }} data-testid="breathing-audio-toggle">
              {audioOn ? <Volume2 className="w-4 h-4" style={{ color }} /> : <VolumeX className="w-4 h-4 text-white/30" />}
            </button>
            <button onClick={running ? handlePause : handleResume}
              className="w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-105 active:scale-95"
              style={{ background: `${color}20`, border: `1px solid ${color}30`, boxShadow: `0 0 20px ${color}15` }}
              data-testid="breathing-pause-play">
              {running ? <Pause className="w-6 h-6" style={{ color }} /> : <Play className="w-6 h-6 ml-0.5" style={{ color }} />}
            </button>
            <div className="w-10" />
          </div>

          <p className="text-white/15 text-xs tabular-nums">
            {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')} / {Math.floor(totalDuration / 60)}:{(totalDuration % 60).toString().padStart(2, '0')}
          </p>
        </div>
      )}
    </div>
  );
};

export default BreathingPlayer;
